import { uniqBy } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { Box, Divider, Stack } from "@mui/material";
import useCategory from "./hooks/use-category.hook";
import { scrollingBox, fillParent } from "utils/base-styles";
import { useIsMobile } from "hooks/use-breakpoint-checks.hook";
import { useSourceSearch } from "features/SourceSearch";
import { useIsTab } from "hooks/is-tab.hook";
import SectionCategory from "./component/section-category";
import { SidebarCard } from "./component/sidebar-card";
import { useSelector } from "react-redux";
import InfiniteScroller from "./component/InfiniteScroller";
import ItemSkeleton from "components/base/ItemSkeleton";
import RelevantSection from "features/News/components/relevant-section";
import PeerReviewedDetail from "features/News/components/peer-reviewed-detail";
import { useLocation, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const CategoryNews = () => {
  const isMobile = useIsMobile();
  const isTab = useIsTab();
  const {
    currentCategory,
    currentSource,
    currentSubCategory,
    getCategory,
    currentSearchTerm,
  } = useSourceSearch();
  const { bookmarkedData } = useSelector((state) => state.data);

  const [loading, setLoading] = useState(true);
  const [categoryData, setcategoryData] = useState([]);
  const [totalResults, setTotalResults] = useState(null);
  const [offset, setOffset] = useState(0);
  const [categoryType, setCategoryType] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const { pathname } = useLocation();

  const scrollRef = useRef(null);
  const { category, search } = useParams();

  const { lastElementRef } = useCategory(
    loading,
    hasMore,
    setOffset,
    offset,
    categoryType,
    currentSubCategory
  );

  function handleDataUpdate(newData, idProp, clear = false) {
    if (clear) {
      setcategoryData([]);
      setTotalResults(null);
      setHasMore(true);
      setOffset(0);
      if (scrollRef.current) {
        scrollRef.current.scrollIntoView({
          behavior: "smooth",
        });
      }
      return;
    } else {
      const items = Array.isArray(newData) ? newData : [];
      setcategoryData((previousData) => {
        const prevLen = previousData.length;
        const combined = uniqBy([...previousData, ...items], idProp);
        setHasMore(combined.length > prevLen);
        return combined;
      });
    }
  }

  useEffect(() => {
    if (bookmarkedData.id && categoryData.length) {
      let matchFn;
      if (category === "Videos") {
        const lastId = bookmarkedData.id.split("/").pop();
        matchFn = (item) => item.id === lastId;
      } else if (currentSource === "books") {
        matchFn = (item) => item.infoLink === bookmarkedData.id;
      } else {
        matchFn = (item) => item.url === bookmarkedData.id;
      }

      const updatedData = categoryData.map((item) =>
        matchFn(item)
          ? {
              ...item,
              collectionSourceId: bookmarkedData.collectionSourceId,
              collectionId: bookmarkedData.collectionId,
              ...(bookmarkedData.collectionMemberships && {
                collectionMemberships: bookmarkedData.collectionMemberships,
              }),
              isCitationAvailable: bookmarkedData.isCitationAvailable,
              isSummaryAvailable: bookmarkedData.isSummaryAvailable,
            }
          : item
      );
      setcategoryData(updatedData);
    }
  }, [
    bookmarkedData.collectionId,
    bookmarkedData.collectionSourceId,
    bookmarkedData.collectionMemberships,
    bookmarkedData.id,
  ]);

  useEffect(() => {
    if (currentCategory) {
      setCategoryType(null);
    } else {
      setCategoryType(currentSource);
    }
    setIsInitialized(true);
  }, [currentCategory, currentSource, category]);

  useEffect(() => {
    setLoading(true);
  }, [pathname]);

  const canShowScroll = isInitialized && categoryType !== "unknown";

  return categoryType !== "unknown" ? (
    <React.Fragment>
      <RelevantSection />
      <Box
        sx={{
          display: { xs: "block", sm: "grid" },
          gridTemplateColumns: { xs: "auto 1fr", md: "20vw 1fr" },
          gap: 2,
          // ...fillParent,
          mt: 2,
        }}
      >
        <Box
          sx={{
            width: { sm: "35vw", md: "auto" },
            position: "sticky",
            top: "16px",
            maxHeight: "calc(100vh - 32px)",
            overflowY: "auto",
          }}
        >
          <SidebarCard
            categoryType={categoryType}
            category={
              getCategory(currentCategory)
                ? getCategory(currentCategory).label
                : ""
            }
            resultCount={categoryData.length}
            totalResults={totalResults}
            showResults={currentSource === "databases" && ["ebsco", "proquest"].includes(String(currentCategory).toLowerCase())}
          />
          <PeerReviewedDetail />
        </Box>

        <Box
          sx={{
            position: "relative",
            mt: { xs: 2, sm: 0 },
          }}
        >
          <Box sx={{}}>
            {canShowScroll && (
              <InfiniteScroller
                offset={offset}
                setLoading={setLoading}
                setcategoryData={handleDataUpdate}
                setoffset={setOffset}
                setShouldLoad={setLoading}
                setHasMore={setHasMore}
                setScrollLoading={setLoading}
                categoryType={categoryType}
                currentCategory={currentCategory}
                setTotalResults={setTotalResults}
              />
            )}
            <Stack direction="row">
              <Box ref={scrollRef} />
              <Box
                sx={{
                  width: "100%",
                }}
              >
                {loading && categoryData.length === 0 ? (
                  <Stack
                    direction="column"
                    sx={{
                      maxWidth: isTab ? "100%" : "70%",
                      pt: 2,
                    }}
                  >
                    {Array.from(Array(5), (e, i) => {
                      return (
                        <React.Fragment key={`news-cat-skeleton-${i}`}>
                          <Box>
                            <ItemSkeleton
                              sx={{
                                minHeight: "200px",
                              }}
                              key={`dummy-${i}`}
                            />
                          </Box>
                          <Divider />
                        </React.Fragment>
                      );
                    })}
                  </Stack>
                ) : (
                  <>
                    <SectionCategory
                      lastElementRef={lastElementRef}
                      categoryData={categoryData}
                      setcategoryData={handleDataUpdate}
                      setHoverIndex={() => false}
                      categoryType={categoryType}
                    />
                    {loading && categoryData.length > 0 && (
                      <Stack
                        direction="column"
                        sx={{
                          maxWidth: isTab ? "100%" : "70%",
                          pt: 2,
                        }}
                      >
                        {Array.from(Array(3), (e, i) => {
                          return (
                            <React.Fragment key={`news-cat-skeleton-${i}`}>
                              <Box>
                                <ItemSkeleton
                                  sx={{
                                    minHeight: "200px",
                                  }}
                                  key={`dummy-${i}`}
                                />
                              </Box>
                              <Divider />
                            </React.Fragment>
                          );
                        })}
                      </Stack>
                    )}
                  </>
                )}
              </Box>
            </Stack>
          </Box>
        </Box>
      </Box>
    </React.Fragment>
  ) : (
    <React.Fragment />
  );
};

export default CategoryNews;
